import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  DisableSettingsEdit,
  SettingsEditState,
  UnSavedSettingsEdit,
} from '@ops/services';
import {
  BankAccount,
  BankAccountService,
  CreditCard,
  CreditCardService,
  EnumService,
  LocalStorageService,
  SessionManagerService,
} from '@ops/shared-types';
import { format } from 'date-fns/format';

import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

const CreditCardDefault = {
  bankAccount: '',
  company: '',
  createdAt: '',
  currency: '',
  currencyCode: '',
  cutOffDateDay: 1,
  deletedAt: '',
  description: '',
  expirationDate: new Date().toISOString(),
  isActive: '',
  isFavorite: false,
  reservedFunds: '',
  settlementDateDay: 1,
  shortName: '',
  lastFourDigits: '',
};

const BankAccountDefault = {
  IBAN: '',
  SWIFT: '',
  address: '',
  bankName: '',
  branchName: '',
  company: '',
  startDate: null,
  createdAt: '',
  currencyCode: '',
  deletedAt: '',
  endDate: '',
  fundedFrom: '',
  startingBalance: 0,
  description: '',
  startingBalanceDate: null,
  correspondentBank: '',
  correspondentSWIFT: '',
  isDebit: '',
  isFavorite: false,
  shortName: '',
};

@Component({
  selector: 'ops-payment-methods',
  templateUrl: './ops-payment-methods.component.html',
  styleUrl: './ops-payment-methods.component.scss',
})
export class OpsPaymentMethodsComponent implements OnInit, OnDestroy {
  savingStep = 0;

  editState$: Observable<boolean> = inject(Store).select(
    SettingsEditState.getEditState
  );

  saveState$: Observable<boolean> = inject(Store).select(
    SettingsEditState.getSaveState
  );

  minExpirationDate: Date = new Date();
  maxExpirationDate: Date = new Date(
    Date.UTC(this.minExpirationDate.getFullYear() + 20, 11, 31, 0, 0, 0, 0)
  );

  subs: Array<Subscription> = [];

  bankAccount = {
    IBAN: '',
    SWIFT: '',
    address: '',
    bankName: '',
    branchName: '',
    company: '',
    startDate: null,
    createdAt: '',
    currencyCode: '',
    deletedAt: '',
    endDate: '',
    fundedFrom: '',
    isDebit: '',
    startingBalance: 0,
    description: '',
    startingBalanceDate: null,
    correspondentBank: '',
    correspondentSWIFT: '',
    isFavorite: false,
    shortName: '',
  };
  creditCardBank;

  creditCard = {
    bankAccount: '',
    company: '',
    createdAt: '',
    currency: '',
    currencyCode: '',
    cutOffDateDay: 1,
    deletedAt: '',
    description: '',
    expirationDate: new Date().toISOString(),
    isActive: '',
    isFavorite: false,
    reservedFunds: '',
    settlementDateDay: 1,
    shortName: '',
    lastFourDigits: '',
  };

  errorsTrack = {};

  mainCurrencies = [];

  cutOffDays = Array.from({ length: 28 }, (_, i) => i + 1 + '');

  constructor(
    private store: Store,
    private bankAccountService: BankAccountService,
    private creditCardService: CreditCardService,
    private ls: LocalStorageService,
    private enumService: EnumService,
    private sessionManager: SessionManagerService,
    private translateService: TranslateService
  ) {}

  delayDisplaying = true;
  delayDisplayingCards = true;

  ngOnInit(): void {
    this.errorsTrack = {};

    const urSub = this.sessionManager
      .getUserRoleObservable()
      .subscribe((userRole: string) => {
        this.userRole = userRole;
      });

    const eState = this.editState$.subscribe((isEditing: any) => {
      this.isPreview = !isEditing;
    });

    const sState = this.saveState$.subscribe((saveChanges: any) => {
      if (saveChanges === true) {
        if (this.creditCards.length === 0) {
          delete this.errorsTrack['CreditCard:shortName'];
          delete this.errorsTrack['CreditCard:description'];
          delete this.errorsTrack['CreditCard:lastFourDigits'];
        }

        const errorFields = Object.keys(this.errorsTrack);

        if (errorFields.length === 0 && this.allValidated) {
          const updateBankAccount$ = this.updateBankAccount();
          const updateCreditCard$ = this.updateCreditCard();

          forkJoin([updateBankAccount$, updateCreditCard$]).subscribe({
            next: ([bankAccountResult, creditCardResult]: any) => {
              // console.log('Bank Account Updated:', bankAccountResult);

              if (bankAccountResult) {
                this.bankAccounsStore = this.bankAccounsStore.filter(
                  (cf) => cf._id !== bankAccountResult._id
                );
                this.bankAccounsStore.push(bankAccountResult);

                this.bankAccounts = [
                  {
                    key: bankAccountResult._id,
                    value: bankAccountResult.shortName,
                  },
                  ...this.bankAccounts.filter(
                    (bA) => bA.key !== bankAccountResult._id
                  ),
                ];

                this.bankAccount = {
                  ...BankAccountDefault,
                  ...bankAccountResult,
                } as unknown as any;

                this.onSelectChange(bankAccountResult.shortName, '');
              }

              if (creditCardResult) {
                this.creditCardsStore = this.creditCardsStore.filter(
                  (cf) => cf._id !== creditCardResult._id
                );
                this.creditCardsStore.push(creditCardResult);

                this.creditCards = [
                  {
                    key: creditCardResult._id,
                    value: creditCardResult.shortName,
                  },
                  ...this.creditCards.filter(
                    (cC) => cC.key !== creditCardResult._id
                  ),
                ];

                this.creditCard = {
                  ...CreditCardDefault,
                  ...creditCardResult,
                } as unknown as any;

                this.onSelectCCChange(creditCardResult.shortName, '');
              }

              // console.log('Credit Card Updated:', creditCardResult);
              this.store.dispatch(new DisableSettingsEdit());
            },
            error: (error) => {
              console.error('An error occurred:', error);
            },
            complete: () => {
              // console.log('All updates completed.');
            },
          });

          this.store.dispatch(new UnSavedSettingsEdit());
        } else {
          const errorsList = errorFields.map((errorKey) => {
            return '\n' + errorKey + ': ' + this.errorsTrack[errorKey];
          });

          if (!this.bankAccount.startDate) {
            errorsList.push('\nBankAccount:startDate: This field is required');
          }

          // if (!this.bankAccount.startingBalanceDate) {
          //   errorsList.push(
          //     '\nBankAccount:startDateBankAccount: This field is required'
          //   );
          // }

          if (!this.bankAccount.currencyCode) {
            errorsList.push(
              '\nBankAccount:currencyCode: This field is required'
            );
          }

          if (!this.creditCard.expirationDate) {
            errorsList.push(
              '\nCreditCard:expirationDate: This field is required'
            );
          }

          if (!this.creditCard.currencyCode) {
            errorsList.push(
              '\nCreditCard:currencyCode: This field is required'
            );
          }

          const joinMessage = errorsList.join('');

          this.store.dispatch(new UnSavedSettingsEdit());

          window.alert(
            this.translateService.instant('windowAlert.opsPaymentMethods.invalidFields', { errors: joinMessage })
          );
        }
      }
    });

    this.mainCurrencies = this.enumService
      .getCurrencyOptionsDetailed()
      .map((cop: any) => {
        return { key: cop.name, value: cop.shortName };
      });

    this.getBankAccounts();
    this.getCreditCards();

    this.subs.push(eState);
    this.subs.push(sState);
    this.subs.push(urSub);
  }

  get allValidated(): boolean {
    const errorFields = Object.keys(this.errorsTrack);
    if (errorFields.length) return false;

    if (!this.bankAccount.startDate) return false;

    // if (!this.bankAccount.startingBalanceDate) return false;

    if (this.creditCards.length > 0 && !this.creditCard.expirationDate)
      return false;

    return true;
  }

  bAccountsSub: Subscription;
  cCreditCardsSub: Subscription;

  getCreditCards() {
    if (this.cCreditCardsSub?.unsubscribe) {
      this.cCreditCardsSub.unsubscribe();
    }

    this.creditCardsStore = [];
    this.creditCards = [];

    // const cards: Array<CreditCard> = await firstValueFrom(
    this.cCreditCardsSub = this.creditCardService
      .getCreditCardsForCompany(this.ls.getCurrentCompany()._id)
      .subscribe((creditCards: CreditCard[]) => {
        this.creditCardsStore = [...creditCards];
        this.creditCard = {
          ...CreditCardDefault,
          ...creditCards[0],
        } as unknown as any;

        this.creditCards = creditCards.map((value: CreditCard) => {
          return {
            key: value._id,
            value: value.shortName,
          };
        });

        if (this.creditCards.length) {
          this.creditCardSelected = this.creditCards[0].value;

          if (this.bankAccounts.length) {
            this.creditCardBank = this.bankAccounts.find(
              (ba) => ba.key === creditCards[0].bankAccount
            ).value;
          }
        }

        this.delayDisplayingCards = false;

        setTimeout(() => {
          this.delayDisplaying = false;
        }, 2000);
      });
  }

  getBankAccounts() {
    if (this.bAccountsSub?.unsubscribe) {
      this.bAccountsSub.unsubscribe();
    }

    this.delayDisplaying = true;
    this.delayDisplayingCards = true;
    this.bankAccountSelected = '';
    this.creditCardBank = undefined;
    this.bankAccounts = [];
    this.bankAccounsStore = [];

    this.bAccountsSub = this.bankAccountService
      .getAllBankAccountsForCompany(this.ls.getCurrentCompany()._id)
      .subscribe((bankAccounts: BankAccount[]) => {
        this.bankAccounts = bankAccounts
          .filter((a) => !a.isDebit)
          .filter(
            (a) =>
              !['DEBIT', 'CASH', 'TEMP'].includes(a.bankName?.toUpperCase())
          )
          .filter(
            (a) =>
              !['DEBIT', 'CASH', 'TEMP'].includes(a.branchName?.toUpperCase())
          );
        this.bankAccounsStore = [...this.bankAccounts];
        this.bankAccount = {
          ...BankAccountDefault,
          ...this.bankAccounts[0],
        } as unknown as any;

        this.bankAccounts = this.bankAccounts.map((value: BankAccount) => {
          return {
            key: value._id,
            value: value.shortName,
          };
        });

        if (this.bankAccounts.length > 0) {
          this.bankAccountSelected = this.bankAccounts[0].value;

          if (this.creditCardsStore.length) {
            this.creditCardBank = this.bankAccounts.find(
              (ba) => ba.key === this.creditCardsStore[0].bankAccount
            ).value;
          }
        }

        this.delayDisplaying = false;

        setTimeout(() => {
          this.delayDisplayingCards = false;
        }, 2000);
      });
  }

  deepDifferences<T extends object>(obj1: T, obj2: T): Partial<T> {
    const differences: Partial<T> = {};

    for (const key in obj1) {
      if (typeof obj1[key] === 'object' && typeof obj2[key] === 'object') {
        const nestedDiff = this.deepDifferences(obj1[key], obj2[key]);
        if (Object.keys(nestedDiff).length > 0) {
          differences[key] = nestedDiff as any;
        }
      } else if (obj1[key] !== obj2[key]) {
        differences[key] = obj2[key];
      }
    }

    for (const key in obj2) {
      if (!(key in obj1)) {
        differences[key] = obj2[key];
      }
    }

    return differences;
  }

  updateCreditCard() {
    if (!this.creditCards.length) {
      return of(null);
    }

    delete this.creditCard.currency;
    const collectDifferences = this.deepDifferences(
      this.creditCard,
      this.creditCardsStore.find(
        (ccsItem) => ccsItem.shortName === this.creditCardSelected
      )
    );

    delete collectDifferences.currency;

    if (Object.keys(collectDifferences).length === 0) {
      return of(null);
    }

    const selectedBankKey = this.bankAccounts.find((ba) => {
      return ba.value === this.creditCardBank;
    }).key;

    const [year, month, date] = this.creditCard.expirationDate
      .split('T')[0]
      .split('-');

    const creditCardClean = {
      ...this.creditCard,
      reservedFunds: 0,
      expirationDate: new Date(Date.UTC(+year, +month - 1, +date, 0, 0, 0, 0)),
      lastFourDigits: parseInt(this.creditCard.lastFourDigits),
      isActive: true,
      bankAccount: selectedBankKey,
    };

    // delete creditCardClean.endDate;
    delete creditCardClean.deletedAt;
    delete creditCardClean.createdAt;
    delete creditCardClean.currency;
    delete creditCardClean.company;
    delete creditCardClean['cutOffDate'];
    delete creditCardClean['settlementDate'];
    delete creditCardClean['_id'];
    delete creditCardClean['updatedAt'];
    delete creditCardClean['__v'];
    delete creditCardClean['bankAccount'];

    return this.creditCardService.updateCreditCard(
      creditCardClean,
      this.creditCard['_id'],
      this.ls.getCurrentCompany()._id
    );
  }

  updateBankAccount() {
    const collectDifferences = this.deepDifferences(
      this.bankAccount,
      this.bankAccounsStore.find(
        (basItem) => basItem.shortName === this.bankAccountSelected
      )
    );

    if (Object.keys(collectDifferences).length === 0) {
      return of(null);
    }

    const bankAccountClean = {
      ...this.bankAccount,
    };

    bankAccountClean.startingBalanceDate =
      this.bankAccount.startingBalanceDate === ''
        ? null
        : this.bankAccount.startingBalanceDate;

    delete bankAccountClean.endDate;
    delete bankAccountClean.deletedAt;
    delete bankAccountClean.createdAt;
    delete bankAccountClean.company;
    delete bankAccountClean.fundedFrom;
    delete bankAccountClean.isDebit;
    delete bankAccountClean['_id'];
    delete bankAccountClean['updatedAt'];
    delete bankAccountClean['__v'];

    return this.bankAccountService.updateBankAccount(
      bankAccountClean,
      this.bankAccount['_id'],
      this.ls.getCurrentCompany()?._id
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());

    if (this.bAccountsSub?.unsubscribe) {
      this.bAccountsSub.unsubscribe();
    }
    if (this.cCreditCardsSub?.unsubscribe) {
      this.cCreditCardsSub.unsubscribe();
    }
  }

  wiseAccount?: any;
  validUntil?: any;
  lastFourDigits?: any;
  paypalAccount?: any;
  bankAccIban?: any;
  connectedBankAccount?: any;
  currencyCode?: any;
  bankDescription?: any;
  cutOffDate?: any;
  bankBranchNo?: any;
  billingPeriod?: any;
  venmoAccount?: any;
  bankSwift?: any;
  bankAccountName?: any;
  fullCardName?: any;
  settlementDate?: any;
  bankName?: any;
  creditCardDescription?: any;

  isPreview?: boolean = true;

  bankAccounsStore = [];
  bankAccounts = [];
  bankAccountSelected: string;

  creditCardsStore = [];
  creditCards = [];
  creditCardSelected: string;

  addingNewBankAccount = false;
  addingNewCreditCard = false;

  userRole = 'guest';

  enableAll() {
    this.isPreview = !this.isPreview;
  }

  onValueChange(selectValue: string, field?: string): void {
    this.bankAccount[field] = selectValue;
  }

  onValueChangeC(selectValue: string, field?: string): void {
    this.creditCard[field] = selectValue;
  }

  addAdditionalBankAccount(event: any) {
    this.addingNewBankAccount = true;
  }

  cancelAddingBankAccount(fetchBankAccounts?: boolean) {
    this.addingNewBankAccount = false;

    if (fetchBankAccounts) {
      this.getBankAccounts();
    }
  }

  get isDisabledForRole(): boolean {
    if (this.userRole === 'admin') {
      return false;
    }

    return true;
  }

  addAdditionalCreditCard(event: any) {
    this.addingNewCreditCard = true;
    //    console.log('Additional bank account');
  }

  cancelAddingCreditCard(fetchCreditCards?: boolean) {
    this.addingNewCreditCard = false;
    if (fetchCreditCards) {
      this.getCreditCards();
    } else if (this.bankAccounts.length > 0) {
      if (!this.bankAccountSelected) {
        this.bankAccountSelected = this.bankAccounts[0].value;
      }

      const matchedBank =
        this.bankAccounsStore.find(
          (ba) => ba.shortName === this.bankAccountSelected
        ) || {};

      this.bankAccount = { ...BankAccountDefault, ...matchedBank };

      if (this.creditCardsStore.length) {
        if (!this.creditCardSelected) {
          this.creditCardSelected = this.creditCards[0].value;
        }

        const matchedCard =
          this.creditCardsStore.find(
            (ba) => ba.shortName === this.creditCardSelected
          ) || {};

        this.creditCardBank = this.bankAccounts.find(
          (ba) => ba.key === this.creditCardsStore[0].bankAccount
        ).value;

        this.creditCard = { ...CreditCardDefault, ...matchedCard };
      }
    }
  }

  addDigitalPaymentService(event: any) {
    //    console.log('Add Digital Payment Service');
  }

  onSelectChange(value: string, field: string) {
    this.bankAccountSelected = value;

    const matchedBank =
      this.bankAccounsStore.find((ba) => ba.shortName === value) || {};

    this.bankAccount = { ...BankAccountDefault, ...matchedBank };
  }

  onSelectChangeForCC(value: string, field: string) {
    this.creditCardBank = value;
  }

  onSelectChangeForCCOpt(value: string, field: string) {
    this.creditCard[field] = value;
  }

  onSelectChangeForBA(value: string, field: string) {
    this.bankAccount[field] = value;
  }

  onSelectCCChange(value: string, field: string) {
    this.creditCardSelected = value;

    const matchedCard =
      this.creditCardsStore.find((ba) => ba.shortName === value) || {};

    this.creditCardBank = this.bankAccounts.find(
      (ba) => ba.key === matchedCard.bankAccount
    ).value;
    this.creditCard = { ...CreditCardDefault, ...matchedCard };
  }

  get creditCardBillingPeriod() {
    const cutOffStart = new Date();
    cutOffStart.setDate(this.creditCard.cutOffDateDay);

    const cutOffEnd = new Date();
    cutOffEnd.setMonth(cutOffEnd.getMonth() + 1);
    cutOffEnd.setDate(cutOffStart.getDate() - 1);

    const settlementDisplayedLTorEQcutOffDateDay =
      this.creditCard.settlementDateDay < this.creditCard.cutOffDateDay;

    if (settlementDisplayedLTorEQcutOffDateDay) {
      const settlementDisplayed = new Date();
      settlementDisplayed.setMonth(settlementDisplayed.getMonth() + 2);
      settlementDisplayed.setDate(this.creditCard.settlementDateDay);

      return `from ${format(cutOffStart, 'MMMdd').toUpperCase()} until ${format(
        cutOffEnd,
        'MMMdd'
      ).toUpperCase()} settled on ${format(
        settlementDisplayed,
        'MMMdd'
      ).toUpperCase()}`;
    }

    const settlementDisplayed = new Date();
    settlementDisplayed.setMonth(settlementDisplayed.getMonth() + 1);
    settlementDisplayed.setDate(this.creditCard.settlementDateDay);

    return `from ${format(cutOffStart, 'MMMdd').toUpperCase()} until ${format(
      cutOffEnd,
      'MMMdd'
    ).toUpperCase()} settled on ${format(
      settlementDisplayed,
      'MMMdd'
    ).toUpperCase()}`;
  }

  onErrorHandler(error: string, field?: string) {
    if (!error || error === '') {
      delete this.errorsTrack[field];
    } else {
      this.errorsTrack[field] = error;
    }
  }
}
